/**
 * Print controller — one document at a time, with the right file name.
 *
 * The cockpit has four printable documents behind five targets (a character
 * sheet, the GM screen session-prep packet, the GM rules reference in its two
 * page shapes, and the battle grid's mat); the first two used to be mounted
 * at once, so "Print" dumped everything into one file: two sheets, the
 * GM screen and the world chart in a single PDF. This module fixes that
 * properly:
 *
 *   · `requestPrint(target, filename)` mounts ONLY that document (the other
 *     print roots are not in the DOM at all), sets `document.title` — which is
 *     what a browser suggests as the PDF file name — calls print, and restores
 *     everything afterwards (on `afterprint`, with a timeout fallback for
 *     browsers that never fire it).
 *   · Each print component subscribes and renders only when it is the target.
 *
 * React state, not CSS: hiding with `display:none` still prints on some
 * browsers and still puts both documents in the file.
 */
import { useSyncExternalStore } from 'react';
import { flushSync } from 'react-dom';
import type { Locale } from '@/domain/shared';

/**
 * The cockpit's five print targets — four documents, the reference counted
 * once: the character sheet, the GM screen packet, the GM rules reference
 * (`gm-reference` portrait binder / `gm-reference-screen` landscape
 * fold-out — two targets over the same panels: different `@page`, different
 * file name), and the battle grid's `battle-grid` battle mat
 * (`<map>-sav-izgarasi`). Only one target is ever active at a time.
 */
export type PrintTarget =
  | 'character'
  | 'character-sheet'
  | 'party-sheets'
  | 'gm-screen'
  | 'gm-reference'
  | 'gm-reference-screen'
  | 'battle-grid';

let current: PrintTarget | null = null;
const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

export function getPrintTarget(): PrintTarget | null {
  return current;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The active print target, or null when nothing is being printed. */
export function usePrintTarget(): PrintTarget | null {
  return useSyncExternalStore(subscribe, getPrintTarget, () => null);
}

/** A file-name-safe slug for the downloaded PDF. */
export function printSlug(value: string): string {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9ğüşıöç]+/gi, '-')
    .replace(/^-|-$/g, '');
  return slug === '' ? 'atla' : slug;
}

/**
 * Print exactly one document. `filename` becomes the suggested PDF name (no
 * extension — the browser adds `.pdf`).
 */
export function requestPrint(target: PrintTarget, filename: string): void {
  if (typeof window === 'undefined') return;
  const previousTitle = document.title;

  // Mount the document synchronously, so it is in the DOM before print().
  flushSync(() => {
    current = target;
    emit();
  });

  let restored = false;
  const restore = (): void => {
    if (restored) return;
    restored = true;
    window.removeEventListener('afterprint', restore);
    document.title = previousTitle;
    flushSync(() => {
      current = null;
      emit();
    });
  };

  window.addEventListener('afterprint', restore);
  document.title = filename;
  window.print();
  // Safari/Firefox in some versions never fire afterprint — do not stay stuck.
  window.setTimeout(restore, 3000);
}

/**
 * Print the GM screen once its rule index can actually be printed.
 *
 * The GM screen's rule index comes from the active locale's ATLA 5e Homebrew book,
 * which @/content now fetches per locale (see loadRuleDocument). `window.print`
 * right after flushSync leaves no time to react, so the rulebook is awaited
 * FIRST — normally already resolved by the boot-time load, so this is a
 * microtask — and printing falls back to "print what we have" if the chunk
 * cannot be fetched at all.
 */
export async function requestGmScreenPrint(filename: string, locale: Locale): Promise<void> {
  try {
    const { loadRuleDocument } = await import('@/content');
    await loadRuleDocument(locale);
  } catch {
    // Offline / chunk failure: still honour the click instead of printing nothing.
  }
  requestPrint('gm-screen', filename);
}
